/**
 * Capsule Studio — Sound Engine
 *
 * Background music: streams the bundled MP3 (bg-music.mp3) via Web Audio API
 * with seamless looping. Falls back to the synthesised arpeggio if the asset
 * hasn't decoded yet (first-load cold-start).
 *
 * SFX (correct / wrong / partial / slide advance / complete / video start)
 * are still synthesised on-the-fly — no audio files needed for them.
 *
 * Paper SFX (see `playInkVoice`) are synthesised
 * the same way, but from filtered noise rather than oscillators, because the
 * sound of a highlighter is not a note: it is a broadband scrape. See the
 * `Paper SFX` section below.
 *
 * On native (iOS / Android) all calls are no-ops; Web Audio is web-only.
 *
 * Public API
 * ──────────
 * setSoundTheme(color)       — pick SFX theme from capsule primary colour
 * startBgMusic(color?)       — start looping background music
 * stopBgMusic()              — fade out and stop background music
 * playCorrect / playWrong / playPartial / playSlideAdvance / playComplete / playVideoStart
 * setMasterVolume(0–1)       — overall volume
 * setMuted(bool)             — mute / unmute everything
 * getMasterVolume()          — current volume (0–1)
 * getMuted()                 — current mute state
 */

/* eslint-disable import-x/no-extraneous-dependencies */
import {Platform} from 'react-native'
import {Asset} from 'expo-asset'

/* eslint-enable import-x/no-extraneous-dependencies */
import {
  type InkStroke,
  type InkTone,
  type InkVerdict,
  type InkVoice,
} from '#/lib/inkVoice'

// ─── Types ────────────────────────────────────────────────────────────────────

type SoundTheme = 'classic' | 'summer' | 'space' | 'nature'

interface ThemeProfile {
  baseHz: number
  waveform: OscillatorType
  reverbAmount: number
  detune: number
}

// ─── Theme profiles (SFX) ─────────────────────────────────────────────────────

const PROFILES: Record<SoundTheme, ThemeProfile> = {
  classic: {baseHz: 440, waveform: 'sine', reverbAmount: 0.15, detune: 0},
  summer: {baseHz: 520, waveform: 'triangle', reverbAmount: 0.25, detune: 5},
  space: {baseHz: 330, waveform: 'sawtooth', reverbAmount: 0.55, detune: -8},
  nature: {baseHz: 392, waveform: 'sine', reverbAmount: 0.3, detune: 3},
}

// ─── Fallback synthesised music (used only while MP3 is loading) ──────────────

const BG_STEP_SEC = 0.55
const BG_NOTE_DUR = 0.5
const BG_LEVEL = 0.15

const BG_PATTERNS: Record<SoundTheme, number[]> = {
  classic: [261.63, 329.63, 392.0, 329.63, 261.63, 392.0, 523.25, 392.0],
  summer: [329.63, 415.3, 493.88, 659.25, 493.88, 415.3, 659.25, 493.88],
  space: [146.83, 174.61, 220.0, 261.63, 220.0, 174.61, 146.83, 220.0],
  nature: [196.0, 246.94, 293.66, 392.0, 293.66, 246.94, 392.0, 246.94],
}

// ─── Colour → theme ────────────────────────────────────────────────────────────

export function themeFromColor(primaryColor: string): SoundTheme {
  const c = (primaryColor ?? '').toLowerCase()
  if (
    c.includes('a855f7') ||
    c.includes('purple') ||
    c.includes('8b5cf6') ||
    c.includes('6366f1')
  )
    return 'space'
  if (c.includes('22c55e') || c.includes('16a34a') || c.includes('green'))
    return 'nature'
  if (
    c.includes('f59e0b') ||
    c.includes('eab308') ||
    c.includes('orange') ||
    c.includes('f97316')
  )
    return 'summer'
  return 'classic'
}

// ─── AudioContext singleton ───────────────────────────────────────────────────

let _ctx: AudioContext | null = null
let _reverbNode: ConvolverNode | null = null

function getCtx(): AudioContext | null {
  if (Platform.OS !== 'web') return null
  const windowObj = typeof window !== 'undefined' ? window : null
  if (
    typeof AudioContext === 'undefined' &&
    (windowObj == null ||
      typeof (windowObj as unknown as Record<string, unknown>)
        .webkitAudioContext === 'undefined')
  )
    return null
  if (!_ctx) {
    try {
      const Ctor: typeof AudioContext = ((
        windowObj as unknown as Record<string, unknown>
      )?.AudioContext ??
        (windowObj as unknown as Record<string, unknown>)
          ?.webkitAudioContext) as typeof AudioContext
      _ctx = new Ctor()
    } catch {
      return null
    }
  }
  if (_ctx.state === 'suspended') {
    void _ctx.resume().catch(() => {})
  }
  return _ctx
}

function getReverb(ctx: AudioContext, amount: number): ConvolverNode | null {
  if (amount === 0) return null
  if (_reverbNode) return _reverbNode
  try {
    const len = ctx.sampleRate * 1.5
    const buf = ctx.createBuffer(2, len, ctx.sampleRate)
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch)
      for (let i = 0; i < len; i++)
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2)
    }
    const node = ctx.createConvolver()
    node.buffer = buf
    _reverbNode = node
    return node
  } catch {
    return null
  }
}

// ─── Master gain (all audio routes through this) ──────────────────────────────

let _masterVol = 0.8
let _muted = false
let _masterNode: GainNode | null = null

// Master-state listeners: every surface (capsule video engine, quiz player)
// subscribes so a mute/volume change made anywhere is mirrored onto whatever
// media element/player is live.
type MasterListener = (s: {muted: boolean; volume: number}) => void
const _masterListeners = new Set<MasterListener>()

export function subscribeMaster(cb: MasterListener): () => void {
  _masterListeners.add(cb)
  return () => {
    _masterListeners.delete(cb)
  }
}

function emitMaster() {
  const s = {muted: _muted, volume: _masterVol}
  for (const cb of _masterListeners) cb(s)
}

function masterNode(): GainNode | null {
  const ctx = getCtx()
  if (!ctx) return null
  if (!_masterNode) {
    _masterNode = ctx.createGain()
    _masterNode.gain.value = _muted ? 0 : _masterVol
    _masterNode.connect(ctx.destination)
  }
  return _masterNode
}

function syncMaster() {
  if (_masterNode) _masterNode.gain.value = _muted ? 0 : _masterVol
  if (_bgGainNode) _bgGainNode.gain.value = _muted ? 0 : _masterVol * BG_LEVEL
}

export function setMasterVolume(v: number) {
  _masterVol = Math.max(0, Math.min(1, v))
  syncMaster()
  emitMaster()
}
export function setMuted(m: boolean) {
  _muted = m
  syncMaster()
  emitMaster()
}
export function getMasterVolume() {
  return _masterVol
}
export function getMuted() {
  return _muted
}
export function isBgMusicRunning() {
  return _bgRunning
}

// ─── Core SFX synth ──────────────────────────────────────────────────────────

function play(
  notes: {hz: number; duration: number; delay?: number; volume?: number}[],
  profile: ThemeProfile,
  gain = 0.35,
) {
  const ctx = getCtx()
  const mgn = masterNode()
  if (!ctx || !mgn) return

  const master = ctx.createGain()
  master.gain.value = gain

  const reverb = getReverb(ctx, profile.reverbAmount)
  if (reverb) {
    const dry = ctx.createGain()
    dry.gain.value = 1 - profile.reverbAmount * 0.6
    const wet = ctx.createGain()
    wet.gain.value = profile.reverbAmount * 0.6
    master.connect(dry)
    master.connect(reverb)
    reverb.connect(wet)
    dry.connect(mgn)
    wet.connect(mgn)
  } else {
    master.connect(mgn)
  }

  const now = ctx.currentTime
  for (const n of notes) {
    const delay = n.delay ?? 0
    const osc = ctx.createOscillator()
    const env = ctx.createGain()
    osc.type = profile.waveform
    osc.frequency.value = n.hz
    osc.detune.value = profile.detune
    env.gain.setValueAtTime(0, now + delay)
    env.gain.linearRampToValueAtTime(n.volume ?? 1, now + delay + 0.008)
    env.gain.exponentialRampToValueAtTime(0.001, now + delay + n.duration)
    osc.connect(env)
    env.connect(master)
    osc.start(now + delay)
    osc.stop(now + delay + n.duration + 0.01)
  }
}

// ─── Paper SFX — a marker dragged across a page ───────────────────────────────
//
// A highlighter is *not* a tone, so it cannot be made with an oscillator. What
// a marker on paper actually is: a short scrape of broadband noise, band-limited
// to the few kHz the fibre of the paper rings at, with an instant contact
// (the nib touching down), a steady middle while it travels, and a fast lift as
// it leaves. That is exactly what the synth below draws — one noise source per
// stroke, through a band-pass that can sweep, so a stroke can *rise* (a stroke
// drawn away from you) or *fall* (one drawn back towards you).
//
// What the strokes *mean* is fixed — one steady stroke is "I have marked this",
// two rising strokes are "that was right", one falling stroke over a low body is
// "that was wrong" — but what they are *made of* is the theme's, not the engine's.
// A hand-kept hardback, an exercise book, a fountain pen, an editor's marker, a
// glass slide and a board of chalk do not sound alike, and one recipe shared by
// nine themes is the same feedback in nine costumes: the learner hears "Taderok"
// rather than the book they are reading. So a theme declares its own
// `InkVoice` (see `#/themes-flashbook/inkVoices`) and this module plays it.
//
// Every layer routes through the same master gain as everything else, so the app's
// existing mute and volume settings already apply to them.

let _noiseBuffer: AudioBuffer | null = null

/** A second of white noise, built once and replayed by every stroke. */
function getNoise(ctx: AudioContext): AudioBuffer {
  if (_noiseBuffer) return _noiseBuffer
  const len = Math.floor(ctx.sampleRate * 0.5)
  const buf = ctx.createBuffer(1, len, ctx.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
  _noiseBuffer = buf
  return buf
}

/** One pass of the nib across the paper. */
type Stroke = {
  /** Centre of the band the paper rings at, in Hz. */
  hz: number
  /** How far the band travels over the stroke. Negative draws backwards. */
  sweep?: number
  /** Seconds. A real stroke is short: 120–260ms. */
  duration: number
  delay?: number
  gain?: number
  /** How narrow the band is. Lower is broader and more "papery". */
  q?: number
}

function playPaper(strokes: Stroke[], gain = 0.5) {
  const ctx = getCtx()
  const mgn = masterNode()
  if (!ctx || !mgn) return

  const out = ctx.createGain()
  out.gain.value = gain
  out.connect(mgn)

  const noise = getNoise(ctx)
  const now = ctx.currentTime

  for (const s of strokes) {
    const at = now + (s.delay ?? 0)
    const level = s.gain ?? 1
    const src = ctx.createBufferSource()
    src.buffer = noise
    src.loop = true

    const band = ctx.createBiquadFilter()
    band.type = 'bandpass'
    band.Q.value = s.q ?? 0.9
    band.frequency.setValueAtTime(s.hz, at)
    if (s.sweep) {
      band.frequency.linearRampToValueAtTime(s.hz + s.sweep, at + s.duration)
    }

    // Rumble has no place in a marker stroke; without this the noise reads as
    // wind rather than as fibre.
    const air = ctx.createBiquadFilter()
    air.type = 'highpass'
    air.frequency.value = 850

    const env = ctx.createGain()
    env.gain.setValueAtTime(0.0001, at)
    // Contact: essentially instant, the way a nib lands.
    env.gain.linearRampToValueAtTime(level, at + 0.012)
    env.gain.setValueAtTime(level, at + s.duration * 0.62)
    // Lift: quick, but not a click.
    env.gain.exponentialRampToValueAtTime(0.0001, at + s.duration)

    src.connect(band)
    band.connect(air)
    air.connect(env)
    env.connect(out)
    src.start(at)
    src.stop(at + s.duration + 0.02)
  }
}

/**
 * A tone, for the themes whose feedback is not only paper.
 *
 * A hand-drawn theme is a nib on a page and nothing else; a space theme's answer
 * is a synth rising, a comic's is a bright zap, a laboratory's is a pip from an
 * instrument. Without this a nine-theme book could only ever be nine markers.
 */
function playTone(t: InkTone, level = 1) {
  const ctx = getCtx()
  const mgn = masterNode()
  if (!ctx || !mgn) return
  const at = ctx.currentTime + (t.delay ?? 0)
  const osc = ctx.createOscillator()
  const env = ctx.createGain()
  osc.type = t.waveform
  osc.frequency.setValueAtTime(t.hz, at)
  if (t.to !== t.hz) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(20, t.to),
      at + t.duration,
    )
  }
  const peak = (t.gain ?? 0.3) * level
  env.gain.setValueAtTime(0.0001, at)
  env.gain.linearRampToValueAtTime(peak, at + Math.min(0.008, t.duration / 4))
  env.gain.exponentialRampToValueAtTime(0.0001, at + t.duration)
  osc.connect(env)
  env.connect(mgn)
  osc.start(at)
  osc.stop(at + t.duration + 0.02)
}

// ─── The theme's own voice ────────────────────────────────────────────────────
//
// The *vocabulary* is `#/lib/inkVoice` — a module with no imports at all, so a theme
// can declare its voice without dragging this engine (Web Audio, `expo-asset`, a
// React Native runtime) into the theme data and into everything that reads it.

/**
 * Play a theme's verdict.
 *
 * Partial credit is neither right nor wrong, so it hears the mark: the nib touches
 * down again and the learner reads the score to find out which half they got.
 */
export function playInkVoice(voice: InkVoice, verdict: InkVerdict) {
  const layers =
    verdict === 'correct'
      ? voice.correct
      : verdict === 'wrong'
        ? voice.wrong
        : voice.mark

  const strokes = layers.filter(
    (l): l is {kind: 'stroke'} & InkStroke => l.kind === 'stroke',
  )
  if (strokes.length) playPaper(strokes, voice.gain)
  /*
   * Tones carry a level of their own, written as the peak they should reach, so
   * they are scaled by how far this theme's voice sits from the reference level —
   * otherwise a theme that halves its strokes' level would keep a full-volume thud
   * underneath them and be quiet except for the one layer nobody wants to hear.
   */
  const level = voice.gain / 0.4
  for (const layer of layers) {
    if (layer.kind === 'tone') playTone(layer, level)
  }
}

// ─── Background music — MP3 loader ───────────────────────────────────────────

let _bgMp3Buffer: AudioBuffer | null = null
let _bgMp3Loading = false

async function loadBgMp3(ctx: AudioContext): Promise<void> {
  if (_bgMp3Buffer || _bgMp3Loading) return
  _bgMp3Loading = true
  try {
    const [asset] = await Asset.loadAsync(
      require('../assets/sounds/bg-music.mp3'),
    )
    if (!asset?.uri) {
      _bgMp3Loading = false
      return
    }
    const resp = await fetch(asset.uri)
    const ab = await resp.arrayBuffer()
    _bgMp3Buffer = await ctx.decodeAudioData(ab)
    _bgMp3Loading = false
    // If startBgMusic was already called while we were loading, start the source now
    if (_bgRunning && _bgGainNode) {
      _startMp3Source(ctx)
    }
  } catch {
    _bgMp3Loading = false
  }
}

// ─── Background music — state ─────────────────────────────────────────────────

let _bgGainNode: GainNode | null = null
let _bgSourceNode: AudioBufferSourceNode | null = null
let _bgRunning = false
let _bgTheme: SoundTheme = 'classic'

// Fallback scheduler (used while MP3 is still loading)
let _bgTimerId: ReturnType<typeof setTimeout> | null = null
let _bgNextTime = 0
let _bgNoteIdx = 0

function _startMp3Source(ctx: AudioContext) {
  if (!_bgMp3Buffer || !_bgGainNode) return
  if (_bgSourceNode) {
    try {
      _bgSourceNode.stop()
    } catch {}
    try {
      _bgSourceNode.disconnect()
    } catch {}
    _bgSourceNode = null
  }
  const src = ctx.createBufferSource()
  src.buffer = _bgMp3Buffer
  src.loop = true
  src.connect(_bgGainNode)
  src.start(0)
  _bgSourceNode = src
}

function _startFallbackScheduler(ctx: AudioContext) {
  if (!_bgRunning || !_bgGainNode) return
  const pattern = BG_PATTERNS[_bgTheme]
  const hz = pattern[_bgNoteIdx % pattern.length]
  _bgNoteIdx++

  const osc = ctx.createOscillator()
  const env = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.value = hz
  osc.detune.value = -4
  env.gain.setValueAtTime(0, _bgNextTime)
  env.gain.linearRampToValueAtTime(0.75, _bgNextTime + 0.06)
  env.gain.setValueAtTime(0.75, _bgNextTime + BG_NOTE_DUR * 0.55)
  env.gain.exponentialRampToValueAtTime(0.001, _bgNextTime + BG_NOTE_DUR * 0.96)
  osc.connect(env)
  env.connect(_bgGainNode)
  osc.start(_bgNextTime)
  osc.stop(_bgNextTime + BG_NOTE_DUR)

  _bgNextTime += BG_STEP_SEC
  const msUntilNext = (_bgNextTime - ctx.currentTime - 0.08) * 1000
  _bgTimerId = setTimeout(
    () => {
      // If MP3 is now ready, swap over to it; otherwise continue synthesising
      if (_bgMp3Buffer && _bgGainNode && _bgRunning) {
        clearTimeout(_bgTimerId!)
        _bgTimerId = null
        _startMp3Source(ctx)
      } else {
        _startFallbackScheduler(ctx)
      }
    },
    Math.max(0, msUntilNext),
  )
}

export function startBgMusic(primaryColor?: string) {
  if (Platform.OS !== 'web') return
  const ctx = getCtx()
  const mgn = masterNode()
  if (!ctx || !mgn) return
  stopBgMusic() // clear any previous session

  _bgTheme = primaryColor ? themeFromColor(primaryColor) : _bgTheme
  _bgRunning = true
  _bgNoteIdx = 0

  _bgGainNode = ctx.createGain()
  _bgGainNode.gain.value = _muted ? 0 : _masterVol * BG_LEVEL
  _bgGainNode.connect(mgn)

  if (_bgMp3Buffer) {
    // MP3 already decoded — start immediately
    _startMp3Source(ctx)
  } else {
    // Start loading; play synthesised arpeggio in the meantime
    void loadBgMp3(ctx)
    _bgNextTime = ctx.currentTime + 0.15
    _startFallbackScheduler(ctx)
  }
}

export function stopBgMusic() {
  _bgRunning = false

  // Stop synthesised fallback scheduler
  if (_bgTimerId != null) {
    clearTimeout(_bgTimerId)
    _bgTimerId = null
  }

  // Stop MP3 source
  if (_bgSourceNode) {
    const src = _bgSourceNode
    _bgSourceNode = null
    try {
      src.stop()
    } catch {}
    try {
      src.disconnect()
    } catch {}
  }

  // Fade out the gain node
  if (_bgGainNode) {
    const node = _bgGainNode
    _bgGainNode = null
    const ctx = _ctx
    if (ctx) {
      try {
        node.gain.setValueAtTime(node.gain.value, ctx.currentTime)
        node.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4)
      } catch {}
      setTimeout(() => {
        try {
          node.disconnect()
        } catch {}
      }, 500)
    } else {
      try {
        node.disconnect()
      } catch {}
    }
  }
}

// ─── SFX public API ──────────────────────────────────────────────────────────

let _theme: ThemeProfile = PROFILES.classic
let _themeName: SoundTheme = 'classic'

export function setSoundTheme(primaryColor: string) {
  _themeName = themeFromColor(primaryColor)
  _theme = PROFILES[_themeName]
  _bgTheme = _themeName
}

export function playCorrect() {
  const b = _theme.baseHz
  play(
    [
      {hz: b * 1.0, duration: 0.12, delay: 0.0},
      {hz: b * 1.25, duration: 0.18, delay: 0.1},
      {hz: b * 1.5, duration: 0.25, delay: 0.2},
    ],
    _theme,
    0.28,
  )
}

export function playWrong() {
  const b = _theme.baseHz * 0.55
  play(
    [
      {hz: b * 1.0, duration: 0.14, delay: 0.0},
      {hz: b * 0.92, duration: 0.2, delay: 0.12},
    ],
    {..._theme, waveform: 'sawtooth'},
    0.22,
  )
}

export function playPartial() {
  const b = _theme.baseHz * 0.85
  play(
    [
      {hz: b * 1.0, duration: 0.1, delay: 0.0},
      {hz: b * 1.15, duration: 0.15, delay: 0.09},
    ],
    _theme,
    0.22,
  )
}

export function playSlideAdvance() {
  const b = _theme.baseHz * 0.75
  play(
    [
      {hz: b, duration: 0.09, delay: 0.0, volume: 0.6},
      {hz: b * 1.1, duration: 0.09, delay: 0.07, volume: 0.4},
    ],
    _theme,
    0.18,
  )
}

export function playComplete() {
  const b = _theme.baseHz
  play(
    [
      {hz: b * 1.0, duration: 0.18, delay: 0.0},
      {hz: b * 1.25, duration: 0.18, delay: 0.14},
      {hz: b * 1.5, duration: 0.18, delay: 0.28},
      {hz: b * 2.0, duration: 0.4, delay: 0.42, volume: 1.2},
    ],
    _theme,
    0.32,
  )
}

export function playVideoStart() {
  const b = _theme.baseHz * 0.7
  play(
    [
      {hz: b, duration: 0.14, delay: 0.0, volume: 0.5},
      {hz: b * 1.2, duration: 0.2, delay: 0.12, volume: 0.6},
    ],
    _theme,
    0.18,
  )
}
